/**
 * Reducer for DbBrowser
 * Handles all database-related state updates
 */

import { ActionTypes } from './Actions';
import Initial_State from './Initial_State';

const Reducer = (state = Initial_State, action) => {
    switch (action.type) {
        // Load DBGroups
        case ActionTypes.LOAD_DBGROUPS_START:
            return {
                ...state,
                loading: { ...state.loading, dbGroups: true },
                errors: { ...state.errors, dbGroups: null }
            };
        
        case ActionTypes.LOAD_DBGROUPS_SUCCESS:
            return {
                ...state,
                dbGroups: action.payload,
                loading: { ...state.loading, dbGroups: false },
                errors: { ...state.errors, dbGroups: null }
            };
        
        case ActionTypes.LOAD_DBGROUPS_ERROR:
            return {
                ...state,
                loading: { ...state.loading, dbGroups: false },
                errors: { ...state.errors, dbGroups: action.payload }
            };
        
        // Load Databases
        case ActionTypes.LOAD_DATABASES_START:
            return {
                ...state,
                loading: { ...state.loading, databases: true },
                errors: { ...state.errors, databases: null }
            };
        
        case ActionTypes.LOAD_DATABASES_SUCCESS:
            return {
                ...state,
                databases: action.payload,
                loading: { ...state.loading, databases: false },
                errors: { ...state.errors, databases: null }
            };
        
        case ActionTypes.LOAD_DATABASES_ERROR:
            return {
                ...state,
                loading: { ...state.loading, databases: false },
                errors: { ...state.errors, databases: action.payload }
            };
        
        // Fetch Accessible Items
        case ActionTypes.FETCH_ACCESSIBLE_ITEMS_START:
            return {
                ...state,
                loading: { ...state.loading, accessibleItems: true },
                errors: { ...state.errors, accessibleItems: null }
            };
        
        case ActionTypes.FETCH_ACCESSIBLE_ITEMS_SUCCESS:
            return {
                ...state,
                serverDatabases: action.payload.databases,
                serverDbGroups: action.payload.dbgroups,
                loading: { ...state.loading, accessibleItems: false },
                errors: { ...state.errors, accessibleItems: null }
            };
        
        case ActionTypes.FETCH_ACCESSIBLE_ITEMS_ERROR:
            return {
                ...state,
                loading: { ...state.loading, accessibleItems: false },
                errors: { ...state.errors, accessibleItems: action.payload }
            };
        
        // Check Sync Status
        case ActionTypes.CHECK_SYNC_STATUS_START:
            return {
                ...state,
                loading: { ...state.loading, syncCheck: true },
                errors: { ...state.errors, syncCheck: null }
            };
        
        case ActionTypes.CHECK_SYNC_STATUS_SUCCESS: {
            const result = action.payload;
            
            // Update DBGroups based on sync check
            const updatedGroups = state.dbGroups.map(group => {
                if (group.storageType !== 'global') return group;
                
                const serverStatus = result.dbgroups[group.name];
                if (!serverStatus) return group;
                
                if (!serverStatus.exists) {
                    // Deleted from server - revert to local
                    return {
                        ...group,
                        storageType: 'local',
                        syncStatus: 'deleted',
                        owners: '',
                        admins: '',
                        contributors: '',
                        readers: '',
                        supervised: ''
                    };
                }
                
                // Check if synced or outdated
                const syncStatus = serverStatus.version === group.version ? 'synced' : 'outdated';
                return {
                    ...group,
                    syncStatus,
                    serverVersion: serverStatus.version,
                    owners: serverStatus.owners,
                    admins: serverStatus.admins,
                    contributors: serverStatus.contributors,
                    readers: serverStatus.readers,
                    supervised: serverStatus.supervised
                };
            });

            // Update Databases based on sync check
            const updatedDatabases = state.databases.map(database => {
                if (database.storageType !== 'global') return database;
                
                const dbKey = `${database.dbGroupName}:${database.name}`;
                const serverStatus = result.databases[dbKey];
                if (!serverStatus) return database;
                
                if (!serverStatus.exists) {
                    // Deleted from server - revert to local
                    return {
                        ...database,
                        storageType: 'local',
                        syncStatus: 'deleted',
                        owners: '',
                        admins: '',
                        contributors: '',
                        readers: '',
                        supervised: ''
                    };
                }
                
                // Check if synced or outdated
                const syncStatus = serverStatus.version === database.version ? 'synced' : 'outdated';
                return {
                    ...database,
                    syncStatus,
                    serverVersion: serverStatus.version,
                    owners: serverStatus.owners,
                    admins: serverStatus.admins,
                    contributors: serverStatus.contributors,
                    readers: serverStatus.readers,
                    supervised: serverStatus.supervised
                };
            });

            // Update IndexedDB with new status
            const dbRequest = indexedDB.open('ShepherdDB');
            dbRequest.onsuccess = (event) => {
                const db = event.target.result;
                
                // Update DBgroups
                const groupsTx = db.transaction(['DBgroups'], 'readwrite');
                const groupsStore = groupsTx.objectStore('DBgroups');
                updatedGroups.forEach(group => groupsStore.put(group));
                
                // Update Databases
                const dbsTx = db.transaction(['Databases'], 'readwrite');
                const dbsStore = dbsTx.objectStore('Databases');
                updatedDatabases.forEach(database => {
                    console.log('[DBBROWSER] Updating database in IndexedDB:', database.name, 'storageType:', database.storageType);
                    dbsStore.put(database);
                });
                
                dbsTx.oncomplete = () => {
                    console.log('[DBBROWSER] All databases updated in IndexedDB');
                };
                
                db.close();
                console.log('[DBBROWSER] Updated sync status in IndexedDB');
            };

            return {
                ...state,
                dbGroups: updatedGroups,
                databases: updatedDatabases,
                loading: { ...state.loading, syncCheck: false },
                errors: { ...state.errors, syncCheck: null },
                lastSyncCheck: Date.now()
            };
        }
        
        case ActionTypes.CHECK_SYNC_STATUS_ERROR:
            return {
                ...state,
                loading: { ...state.loading, syncCheck: false },
                errors: { ...state.errors, syncCheck: action.payload }
            };
        
        // Push DBGroup
        case ActionTypes.PUSH_DBGROUP_START:
            return {
                ...state,
                operations: { ...state.operations, pushing: action.payload.id },
                errors: { ...state.errors, push: null }
            };
        
        case ActionTypes.PUSH_DBGROUP_SUCCESS: {
            const updatedGroup = action.payload;
            
            // Update in state
            const updatedGroups = state.dbGroups.map(g => 
                g.id === updatedGroup.id ? updatedGroup : g
            );
            
            // Update in IndexedDB
            const dbRequest = indexedDB.open('ShepherdDB');
            dbRequest.onsuccess = (event) => {
                const db = event.target.result;
                const transaction = db.transaction(['DBgroups'], 'readwrite');
                const objectStore = transaction.objectStore('DBgroups');
                objectStore.put(updatedGroup);
                transaction.oncomplete = () => db.close();
            };
            
            return {
                ...state,
                dbGroups: updatedGroups,
                operations: { ...state.operations, pushing: null },
                errors: { ...state.errors, push: null }
            };
        }
        
        case ActionTypes.PUSH_DBGROUP_ERROR:
            return {
                ...state,
                operations: { ...state.operations, pushing: null },
                errors: { ...state.errors, push: action.payload }
            };
        
        // Push Database
        case ActionTypes.PUSH_DATABASE_START:
            return {
                ...state,
                operations: { ...state.operations, pushing: action.payload.id },
                errors: { ...state.errors, push: null }
            };
        
        case ActionTypes.PUSH_DATABASE_SUCCESS: {
            const updatedDatabase = action.payload;
            
            // Update in state
            const updatedDatabases = state.databases.map(db => 
                db.id === updatedDatabase.id ? updatedDatabase : db
            );
            
            // Update in IndexedDB
            const dbRequest = indexedDB.open('ShepherdDB');
            dbRequest.onsuccess = (event) => {
                const db = event.target.result;
                const transaction = db.transaction(['Databases'], 'readwrite');
                const objectStore = transaction.objectStore('Databases');
                objectStore.put(updatedDatabase);
                transaction.oncomplete = () => db.close();
            };
            
            return {
                ...state,
                databases: updatedDatabases,
                operations: { ...state.operations, pushing: null },
                errors: { ...state.errors, push: null }
            };
        }
        
        case ActionTypes.PUSH_DATABASE_ERROR:
            return {
                ...state,
                operations: { ...state.operations, pushing: null },
                errors: { ...state.errors, push: action.payload }
            };
        
        // Download Database
        case ActionTypes.DOWNLOAD_DATABASE_START:
            return {
                ...state,
                operations: { ...state.operations, downloading: action.payload.id },
                errors: { ...state.errors, download: null }
            };
        
        case ActionTypes.DOWNLOAD_DATABASE_SUCCESS:
            return {
                ...state,
                databases: [...state.databases, action.payload],
                operations: { ...state.operations, downloading: null },
                errors: { ...state.errors, download: null }
            };
        
        case ActionTypes.DOWNLOAD_DATABASE_ERROR:
            return {
                ...state,
                operations: { ...state.operations, downloading: null },
                errors: { ...state.errors, download: action.payload }
            };
        
        // Download DBGroup
        case ActionTypes.DOWNLOAD_DBGROUP_START:
            return {
                ...state,
                operations: { ...state.operations, downloading: action.payload.id },
                errors: { ...state.errors, download: null }
            };
        
        case ActionTypes.DOWNLOAD_DBGROUP_SUCCESS:
            return {
                ...state,
                dbGroups: [...state.dbGroups, action.payload],
                operations: { ...state.operations, downloading: null },
                errors: { ...state.errors, download: null }
            };
        
        case ActionTypes.DOWNLOAD_DBGROUP_ERROR:
            return {
                ...state,
                operations: { ...state.operations, downloading: null },
                errors: { ...state.errors, download: action.payload }
            };
        
        // Fetch User Names
        case ActionTypes.FETCH_USER_NAMES_START:
            return {
                ...state,
                loading: { ...state.loading, userNames: true },
                errors: { ...state.errors, userNames: null }
            };
        
        case ActionTypes.FETCH_USER_NAMES_SUCCESS:
            return {
                ...state,
                userNames: { ...state.userNames, ...action.payload },
                loading: { ...state.loading, userNames: false },
                errors: { ...state.errors, userNames: null }
            };
        
        case ActionTypes.FETCH_USER_NAMES_ERROR:
            return {
                ...state,
                loading: { ...state.loading, userNames: false },
                errors: { ...state.errors, userNames: action.payload }
            };
        
        // Update DBGroup
        case ActionTypes.UPDATE_DBGROUP: {
            const updatedGroups = state.dbGroups.map(g =>
                g.id === action.payload.id ? action.payload : g
            );
            return {
                ...state,
                dbGroups: updatedGroups
            };
        }
        
        // Update Database
        case ActionTypes.UPDATE_DATABASE: {
            const updatedDatabases = state.databases.map(db =>
                db.id === action.payload.id ? action.payload : db
            );
            return {
                ...state,
                databases: updatedDatabases
            };
        }
        
        // Clear Error
        case ActionTypes.CLEAR_ERROR:
            return {
                ...state,
                errors: { ...state.errors, [action.payload]: null }
            };
        
        // Search Users
        case ActionTypes.SEARCH_USERS_START:
            return {
                ...state,
                loading: { ...state.loading, searchingUsers: true },
                errors: { ...state.errors, searchUsers: null }
            };
        
        case ActionTypes.SEARCH_USERS_SUCCESS:
            return {
                ...state,
                loading: { ...state.loading, searchingUsers: false },
                errors: { ...state.errors, searchUsers: null }
            };
        
        case ActionTypes.SEARCH_USERS_ERROR:
            return {
                ...state,
                loading: { ...state.loading, searchingUsers: false },
                errors: { ...state.errors, searchUsers: action.payload }
            };
        
        // Update Database Permissions
        case ActionTypes.UPDATE_DB_PERMISSIONS_START:
            return {
                ...state,
                loading: { ...state.loading, updatingPermissions: true },
                errors: { ...state.errors, permissions: null }
            };
        
        case ActionTypes.UPDATE_DB_PERMISSIONS_SUCCESS: {
            const { databaseName, dbGroupName, permissions, version } = action.payload;
            
            // Update local databases
            const updatedDatabases = state.databases.map(db => {
                if (db.name === databaseName && db.dbGroupName === dbGroupName) {
                    return {
                        ...db,
                        owners: permissions.owners.join(','),
                        admins: permissions.admins.join(','),
                        contributors: permissions.contributors.join(','),
                        readers: permissions.readers.join(','),
                        supervised: permissions.supervised.join(','),
                        version
                    };
                }
                return db;
            });
            
            // Update server databases
            const updatedServerDatabases = state.serverDatabases.map(db => {
                if (db.name === databaseName && db.dbGroupName === dbGroupName) {
                    return {
                        ...db,
                        owners: permissions.owners.join(','),
                        admins: permissions.admins.join(','),
                        contributors: permissions.contributors.join(','),
                        readers: permissions.readers.join(','),
                        supervised: permissions.supervised.join(','),
                        version
                    };
                }
                return db;
            });
            
            return {
                ...state,
                databases: updatedDatabases,
                serverDatabases: updatedServerDatabases,
                loading: { ...state.loading, updatingPermissions: false },
                errors: { ...state.errors, permissions: null }
            };
        }
        
        case ActionTypes.UPDATE_DB_PERMISSIONS_ERROR:
            return {
                ...state,
                loading: { ...state.loading, updatingPermissions: false },
                errors: { ...state.errors, permissions: action.payload }
            };
        
        // Update DBGroup Permissions
        case ActionTypes.UPDATE_DBGROUP_PERMISSIONS_START:
            return {
                ...state,
                loading: { ...state.loading, updatingPermissions: true },
                errors: { ...state.errors, permissions: null }
            };
        
        case ActionTypes.UPDATE_DBGROUP_PERMISSIONS_SUCCESS: {
            const { dbGroupName, permissions, version } = action.payload;
            
            // Update local dbGroups
            const updatedDbGroups = state.dbGroups.map(group => {
                if (group.name === dbGroupName) {
                    return {
                        ...group,
                        owners: permissions.owners.join(','),
                        admins: permissions.admins.join(','),
                        contributors: permissions.contributors.join(','),
                        readers: permissions.readers.join(','),
                        supervised: permissions.supervised.join(','),
                        version
                    };
                }
                return group;
            });
            
            // Update server dbGroups
            const updatedServerDbGroups = state.serverDbGroups.map(group => {
                if (group.name === dbGroupName) {
                    return {
                        ...group,
                        owners: permissions.owners.join(','),
                        admins: permissions.admins.join(','),
                        contributors: permissions.contributors.join(','),
                        readers: permissions.readers.join(','),
                        supervised: permissions.supervised.join(','),
                        version
                    };
                }
                return group;
            });
            
            return {
                ...state,
                dbGroups: updatedDbGroups,
                serverDbGroups: updatedServerDbGroups,
                loading: { ...state.loading, updatingPermissions: false },
                errors: { ...state.errors, permissions: null }
            };
        }
        
        case ActionTypes.UPDATE_DBGROUP_PERMISSIONS_ERROR:
            return {
                ...state,
                loading: { ...state.loading, updatingPermissions: false },
                errors: { ...state.errors, permissions: action.payload }
            };
        
        // Yjs real-time sync status updates
        case ActionTypes.YJS_SYNC_STATUS_UPDATE: {
            const { databaseId, status } = action.payload;
            return {
                ...state,
                yjsSyncStatus: {
                    ...state.yjsSyncStatus,
                    [databaseId]: status
                }
            };
        }
        
        case ActionTypes.YJS_PENDING_CHANGES_UPDATE: {
            const { databaseId, pendingCount } = action.payload;
            return {
                ...state,
                yjsPendingChanges: {
                    ...state.yjsPendingChanges,
                    [databaseId]: pendingCount
                }
            };
        }
        
        default:
            return state;
    }
};

export default Reducer;
